import { KnowledgeBaseType } from 'apis/knoledgeBases/types/knowledgeBases.types';
import {
  CreateDixaKnowledgeApiArg,
  CreateElevioKnowledgeApiArg,
  CreateKnowledgeBaseApiResponse,
  CreateWebsiteApiArg,
  DeleteKnowledgeBaseApiArg,
  ReadKnowledgeBaseApiArg,
  ReadKnowledgeBaseByIdApiArg,
  ReadKnowledgeBaseByIdApiResponse,
  ReadKnowledgeBasesApiResponse,
  SyncKnowledgeApiArg,
  UpdateDixaKnowledgeKBApiArg,
  UpdateElevioKBApiArg,
  UpdateKnowledgeBaseApiResponse,
  UpdateWebsiteKBApiArg,
} from 'apis/knoledgeBases/types/knowledgeBasesApi.types';
import {
  transformKnowledgeBaseResponse,
  transformKnowledgeBasesResponse,
} from 'apis/knoledgeBases/utils/transformResponse';
import { api } from 'app/app.api';

export const knowledgeBasesApi = api
  .enhanceEndpoints({
    addTagTypes: ['knowledgeBase'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readKnowledgeBases: builder.query<
        ReadKnowledgeBasesApiResponse,
        ReadKnowledgeBaseApiArg
      >({
        query: ({ organizationId }) => ({
          version: 1,
          path: 'knowledge-bases',
          params: {
            organization_id: organizationId,
          },
        }),
        transformResponse: transformKnowledgeBasesResponse,
        providesTags: ['knowledgeBase'],
      }),
      readKnowledgeBaseById: builder.query<
        ReadKnowledgeBaseByIdApiResponse,
        ReadKnowledgeBaseByIdApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `knowledge-bases/${id}`,
        }),
        transformResponse: transformKnowledgeBaseResponse,
        providesTags: ['knowledgeBase'],
      }),
      deleteKnowledgeBase: builder.mutation<void, DeleteKnowledgeBaseApiArg>({
        query: ({ id }) => ({
          version: 1,
          path: `knowledge-bases/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['knowledgeBase'],
      }),
      updateKnowledgeBase: builder.mutation<
        UpdateKnowledgeBaseApiResponse,
        {
          type: KnowledgeBaseType;
          id: string;
          identity:
            | UpdateElevioKBApiArg
            | UpdateDixaKnowledgeKBApiArg
            | UpdateWebsiteKBApiArg;
        }
      >({
        query: ({ type, id, identity }) => {
          let body = {};
          if (type === 'elevio') {
            const { api_key, token } = identity as UpdateElevioKBApiArg;
            body = { api_key, token };
          } else if (type === 'dixa_knowledge') {
            const { external_source_id } =
              identity as UpdateDixaKnowledgeKBApiArg;
            body = { external_source_id };
          } else if (type === 'website') {
            const { name, excluded_urls } = identity as UpdateWebsiteKBApiArg;
            body = { name, excluded_urls };
          }
          return {
            version: 1,
            path: `knowledge-bases/${id}/${type}`,
            method: 'PUT',
            body,
          };
        },
        transformResponse: transformKnowledgeBaseResponse,
        invalidatesTags: ['knowledgeBase'],
      }),
      createKnowledgeBase: builder.mutation<
        CreateKnowledgeBaseApiResponse,
        {
          type: KnowledgeBaseType;
          identity:
            | CreateElevioKnowledgeApiArg
            | CreateDixaKnowledgeApiArg
            | CreateWebsiteApiArg;
        }
      >({
        query: ({ type, identity }) => {
          let body = {};
          if (type === 'elevio') {
            const { api_key, token, organization_id } =
              identity as CreateElevioKnowledgeApiArg;
            body = { api_key, token, organization_id };
          } else if (type === 'dixa_knowledge') {
            const { external_source_id, organization_id } =
              identity as CreateDixaKnowledgeApiArg;
            body = { external_source_id, organization_id };
          } else if (type === 'website') {
            const { organization_id, name, url } =
              identity as CreateWebsiteApiArg;
            body = { organization_id, name, url };
          }
          return {
            version: 1,
            path: `knowledge-bases/${type}`,
            method: 'POST',
            body,
          };
        },
        transformResponse: transformKnowledgeBaseResponse,
        invalidatesTags: ['knowledgeBase'],
      }),
      syncKnowledgeBase: builder.mutation<void, SyncKnowledgeApiArg>({
        query: ({ id, force_reload = false }) => ({
          version: 1,
          path: `knowledge-bases/${id}/sync`,
          method: 'POST',
          params: {
            force_reload,
          },
        }),
        transformResponse: transformKnowledgeBaseResponse,
        invalidatesTags: ['knowledgeBase'],
      }),
    }),
  });
